// IncompleteSetupBanner — shown on SchoolDashboard for users (verified OR
// not) whose setup is < 100%. Lists the missing sections (with up to 3
// concrete missing-field hints each) and links to /setup.
//
// For UNVERIFIED users: also surfaces the "Mark setup complete" CTA which
// starts the 1-hour auto-activation timer.
// For VERIFIED users (legacy iter108 auto-verifies): purely informational —
// they keep their access but are nudged to fill the remaining fields so
// reports stay accurate.
import { useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CheckCircle2, AlertCircle, Loader2, Settings2, ArrowRight, Sparkles } from "lucide-react";
import { usePolling } from "@/lib/usePolling";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTION_LABELS = {
  schoolIdentity: { en: "School Identity",  od: "ବିଦ୍ୟାଳୟ ବିବରଣୀ",     to: "/setup?tab=profile" },
  vendors:        { en: "Vendor Details",   od: "ବ୍ୟବସାୟୀ ବିବରଣୀ",      to: "/setup?tab=vendors" },
  cchs:           { en: "CCH (Cook) Details","od": "ରୋଷେୟା ବିବରଣୀ",     to: "/setup?tab=cchs" },
  enrollment:     { en: "Year-wise Enrollment", od: "ବର୍ଷ-ଅନୁଯାୟୀ ନାମାଙ୍କନ", to: "/setup?tab=enrollment" },
};

export default function IncompleteSetupBanner({ verified, onMarkedComplete }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [marking, setMarking] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/setup-status`);
      setStatus(resp.data);
    } catch (err) {
      console.warn("setup-status fetch failed:", err?.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // iter150-review — polling throttled to 30 s and only runs while
  // the tab is visible + online (via usePolling).
  usePolling(fetchStatus, 30_000);

  const markComplete = async () => {
    setMarking(true);
    try {
      await axios.post(`${API}/subscriptions/mark-setup-complete`);
      toast.success("Setup complete! Auto-verification in 1 hour.");
      if (onMarkedComplete) await onMarkedComplete();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not mark setup complete");
    } finally {
      setMarking(false);
    }
  };

  if (loading || !status || status.isComplete) return null;

  const percent = status.percent || 0;
  const stage = status.stage ?? 0;
  const total = status.totalSections ?? 4;
  const sections = status.sections || {};
  const missingBySection = status.missingBySection || {};

  return (
    <section
      className="mb-6 overflow-hidden rounded-2xl border-2 border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 shadow-sm"
      data-testid="incomplete-setup-banner"
    >
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-amber-200 bg-amber-100/40 px-5 py-3">
        <div className="flex items-center gap-3">
          <AlertCircle className="h-6 w-6 shrink-0 text-amber-600" />
          <div>
            <p className="text-[0.7rem] font-semibold uppercase tracking-[0.18em] text-amber-700">
              Setup {stage}/{total} sections complete
            </p>
            <h2 className="font-heading text-base font-semibold text-amber-900 sm:text-lg" data-testid="incomplete-setup-headline">
              {verified
                ? "Your account is active — but please fill these remaining details for accurate reports"
                : "Complete the remaining setup sections to activate your account"}
            </h2>
          </div>
        </div>
        <span className="rounded-full bg-amber-600 px-3 py-1 font-mono text-xs font-bold text-white" data-testid="incomplete-setup-percent">
          {percent}%
        </span>
      </div>

      {/* Progress bar */}
      <div className="h-2 bg-amber-100">
        <div
          className="h-full bg-amber-500 transition-all duration-500"
          style={{ width: `${percent}%` }}
          data-testid="incomplete-setup-progress"
        />
      </div>

      {/* Sections grid */}
      <div className="grid grid-cols-1 gap-3 px-5 py-4 sm:grid-cols-2">
        {Object.entries(SECTION_LABELS).map(([key, meta]) => {
          const ok = sections[key];
          const missing = missingBySection[key] || [];
          if (ok) {
            return (
              <Link
                to={meta.to}
                key={key}
                className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm transition hover:bg-emerald-100"
                data-testid={`incomplete-section-${key}-ok`}
              >
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-emerald-900">{meta.en}</p>
                  <p className="text-xs text-emerald-700">{meta.od}</p>
                  <p className="mt-0.5 text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-emerald-600">Complete ✓</p>
                </div>
              </Link>
            );
          }
          return (
            <Link
              to={meta.to}
              key={key}
              className="flex items-start gap-2 rounded-lg border-2 border-amber-300 bg-white px-3 py-2.5 text-sm transition hover:border-amber-400 hover:bg-amber-50"
              data-testid={`incomplete-section-${key}`}
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-amber-900">{meta.en}</p>
                <p className="text-xs text-amber-700">{meta.od}</p>
                <ul className="mt-1.5 space-y-0.5 text-xs text-amber-800">
                  {missing.slice(0, 3).map((m, i) => (
                    <li key={i} className="flex gap-1">
                      <span>•</span>
                      <span className="truncate">{m}</span>
                    </li>
                  ))}
                  {missing.length > 3 && (
                    <li className="font-semibold text-amber-700">…and {missing.length - 3} more</li>
                  )}
                </ul>
                <p className="mt-1.5 inline-flex items-center gap-1 text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-amber-700">
                  Open section <ArrowRight className="h-3 w-3" />
                </p>
              </div>
            </Link>
          );
        })}
      </div>

      {/* Footer action */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-amber-200 bg-amber-50/60 px-5 py-3">
        <p className="text-xs text-amber-900">
          💡 <b>Tip:</b> Once all 4 sections are 100% complete, your account auto-activates within 1 hour.
        </p>
        <div className="flex gap-2">
          <Button asChild variant="outline" size="sm" className="border-amber-400 text-amber-700 hover:bg-amber-100" data-testid="incomplete-open-setup-btn">
            <Link to="/setup">
              <Settings2 className="mr-2 h-4 w-4" /> Open Setup
            </Link>
          </Button>
          {!verified && status.isComplete === false && stage === total && (
            <Button
              onClick={markComplete}
              disabled={marking}
              size="sm"
              className="bg-emerald-600 text-white hover:bg-emerald-700"
              data-testid="incomplete-mark-complete-btn"
            >
              {marking ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
              Mark complete & activate
            </Button>
          )}
        </div>
      </div>
    </section>
  );
}
